import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  IconButton,
  Grid,
  Box,
  Typography,
  Drawer,
  Paper,
  Stack,
  Toolbar,
  Button,
  Backdrop,
  CircularProgress,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useUser } from '@/providers';
import { ArrowBack } from '@mui/icons-material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import VolumeField from './VolumeField';
import { fetchVolumes } from '../../../actions/volumes';

export default function VolumeDrawer({
  open,
  handleClose,
  initialValues,
  setValue,
}) {
  const dispatch = useDispatch();
  const user = useUser();
  const volumes = useSelector((state) => state.volumes);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const safe_initial_values = Array.isArray(initialValues) ? initialValues : [];

  const [selectedVolumes, setSelectedVolumes] = useState(
    safe_initial_values.map((volume) => ({ ...volume })),
  );

  const [volume_errors, setVolumeErrors] = useState(
    new Array(safe_initial_values.length).fill(false),
  );

  const selectedVolumesRef = useRef(
    safe_initial_values.map((volume) => ({ ...volume })),
  );

  useEffect(() => {
    selectedVolumesRef.current = selectedVolumes;
  }, [selectedVolumes]);

  useEffect(() => {
    const safe = Array.isArray(initialValues) ? initialValues : [];
    const new_volumes = safe.map((volume) => ({ ...volume }));
    selectedVolumesRef.current = new_volumes;
    setSelectedVolumes(new_volumes);
  }, [initialValues]);

  useEffect(() => {
    (async () => {
      if (volumes === null) {
        if (user?.selected_project) {
          dispatch(fetchVolumes(user.selected_project));
        }
        return;
      }
      setLoading(false);
    })();
  }, [dispatch, volumes, user?.selected_project]);

  const volumeErrorCheck = useCallback((volumes) => {
    const names = volumes.map((volume) => {
      if (volume.name) {
        return volume.name.toLowerCase().replace(' ', '_');
      }
      return undefined;
    });
    const dup_names = names.filter((e, i, a) => a.indexOf(e) !== i);
    const errors = volumes.map((volume) => {
      if (volume.name) {
        return dup_names.includes(volume.name.toLowerCase().replace(' ', '_'));
      }
      return true;
    });
    return errors;
  }, []);

  const calculateError = useCallback(
    (volume_errors) => {
      const errors_set = new Set([...volume_errors]);
      if (
        errors_set.size > 1 ||
        ([...errors_set][0] && [...errors_set][0] !== false)
      ) {
        setError(true);
      } else {
        setError(false);
      }
    },
    [setError],
  );

  const setVolumeError = useCallback(
    (i, error) => {
      setVolumeErrors((prevErrors) => {
        const new_errors = [...prevErrors];
        new_errors[i] = error;

        // Recalculate duplicates using current selectedVolumes from ref
        const duplicateErrors = volumeErrorCheck(selectedVolumesRef.current);
        const combinedErrors = new_errors.map((err, idx) => {
          // If there's an explicit error (string), keep it, otherwise use duplicate check
          return err && err !== false ? err : duplicateErrors[idx];
        });
        calculateError(combinedErrors);
        return combinedErrors;
      });
    },
    [calculateError, volumeErrorCheck],
  );

  const addVolume = useCallback(() => {
    const new_volumes = [
      ...selectedVolumes,
      { volume_id: '', id: '' },
    ];
    selectedVolumesRef.current = new_volumes;
    setSelectedVolumes(new_volumes);
    const new_errors = [...volume_errors, 'Volume cannot be blank'];
    setVolumeErrors(new_errors);
    calculateError(new_errors);
  }, [
    selectedVolumes,
    volume_errors,
    setSelectedVolumes,
    calculateError,
    setVolumeErrors,
  ]);

  const removeVolume = useCallback(
    (i) => {
      const new_volumes = [...selectedVolumes];
      new_volumes.splice(i, 1);
      selectedVolumesRef.current = new_volumes;

      setSelectedVolumes(new_volumes);
      const new_errors = [...volume_errors];
      new_errors.splice(i, 1);

      // Recheck for duplicates after removal
      const duplicateErrors = volumeErrorCheck(new_volumes);
      const combinedErrors = new_errors.map((err, idx) => {
        return err && err !== false ? err : duplicateErrors[idx];
      });

      setVolumeErrors(combinedErrors);
      calculateError(combinedErrors);
    },
    [
      selectedVolumes,
      volume_errors,
      setSelectedVolumes,
      setVolumeErrors,
      calculateError,
      volumeErrorCheck,
    ],
  );

  const setVolumeValue = useCallback(
    (i, values) => {
      setSelectedVolumes((prevVolumes) => {
        const new_volumes = [...prevVolumes];
        new_volumes[i] = values;
        // Update ref immediately
        selectedVolumesRef.current = new_volumes;

        // Recheck for duplicates after value change
        setVolumeErrors((prevErrors) => {
          const duplicateErrors = volumeErrorCheck(new_volumes);
          const currentErrors = [...prevErrors];
          // Only update error if it's not already an explicit error (string)
          currentErrors[i] =
            currentErrors[i] && currentErrors[i] !== false
              ? currentErrors[i]
              : duplicateErrors[i];
          calculateError(currentErrors);
          return currentErrors;
        });

        return new_volumes;
      });
    },
    [volumeErrorCheck, calculateError],
  );

  const submit = useCallback(() => {
    // Enrich volumes with name and size from Redux store
    const enrichedVolumes = selectedVolumes.map((volume) => {
      if (!volume.volume_id && !volume.id) {
        return volume;
      }
      const volumeId = volume.volume_id || volume.id;
      const fullVolume = volumes?.find((v) => v.id === volumeId);
      return {
        ...volume,
        name: fullVolume ? fullVolume.name : volume.name || '',
        size: fullVolume ? fullVolume.size || fullVolume.used_size : volume.size,
      };
    });
    setValue(enrichedVolumes);
    handleClose();
  }, [selectedVolumes, setValue, handleClose, volumes]);

  return (
    <Drawer
      sx={{ height: '100%' }}
      anchor='right'
      open={open}
      onClose={handleClose}
    >
      <Grid
        container
        spacing={2}
        direction='row'
        sx={{
          display: 'flex',
          width: '100%',
          height: '100%',
          margin: '0px',
          padding: '0px 0px 0px 0px !important',
          '.MuiGrid-root': {
            padding: '0px 0px 0px 0px',
          },
          maxWidth: '550px',
        }}
      >
        <Grid
          container
          justifyContent='flex-start'
          sx={{
            maxHeight: '75px',
          }}
        >
          <Paper
            sx={{
              display: 'flex',
              height: '100%',
              width: '100%',
            }}
            elevation={1}
          >
            <Toolbar>
              <Stack spacing={2} direction='row' alignItems='center'>
                <IconButton onClick={() => handleClose()}>
                  <ArrowBack />
                </IconButton>
                <Typography variant='h5'>Manage Volumes</Typography>
              </Stack>
            </Toolbar>
          </Paper>
        </Grid>
        <Grid
          size={{ xs: 12, md: 12, lg: 12 }}
          sx={{
            display: 'flex',
            height: '100%',
            width: '100%',
          }}
        >
          <Backdrop open={loading}>
            <CircularProgress />
          </Backdrop>
          <Box sx={{ padding: '16px' }}>
            <Stack spacing={2} direction='column' alignItems='center'>
              {selectedVolumes.map((value, index) => {
                return (
                  <VolumeField
                    key={`volume-${index}`}
                    index={index}
                    value={value}
                    error={volume_errors[index]}
                    setError={setVolumeError}
                    setValue={setVolumeValue}
                    remove={removeVolume}
                  />
                );
              })}
              <Button
                variant='outlined'
                color='brand'
                onClick={() => addVolume()}
              >
                <Stack direction='row' spacing={1} alignItems='center'>
                  <AddCircleOutlineIcon />
                  <Typography variant='button' display='block' gutterBottom>
                    Add Volume
                  </Typography>
                </Stack>
              </Button>
            </Stack>
          </Box>
        </Grid>
        <Grid
          container
          sx={{
            maxHeight: '50px',
            position: 'absolute',
            bottom: '0px',
            width: '100%',
          }}
        >
          <Paper sx={{ display: 'flex', width: '100%' }} elevation={1}>
            <Toolbar>
              <Stack spacing={2} direction='row'>
                <Button
                  variant='contained'
                  color='brand'
                  onClick={submit}
                  disabled={error}
                >
                  Save
                </Button>
                <Button
                  variant='outlined'
                  onClick={() => {
                    const safe = Array.isArray(initialValues) ? initialValues : [];
                    setSelectedVolumes(safe.map((volume) => ({ ...volume })));
                    setVolumeErrors(new Array(safe.length).fill(false));
                    setError(false);
                    handleClose();
                  }}
                >
                  Cancel
                </Button>
              </Stack>
            </Toolbar>
          </Paper>
        </Grid>
      </Grid>
    </Drawer>
  );
}

